import React, { useState, useEffect } from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import { cx } from '@emotion/css';
import { keyframes } from '@emotion/react';
import theme from '@cube3/design-tokens/build/js/dark-tokens';
import { Theme } from '../../../theme/themes';
import { isMobile } from 'react-device-detect';

/*Toggle is used to view or switch between two or more possible states.*/

export interface ToggleProps {
  /**Disabled component*/
  disabled?: boolean;
  /**When the toggle is switch*/
  onChange?(newState: boolean): void;
  /**Indicates whether the toggle is switched or not*/
  value?: boolean;
  /**How long we want the animation to take*/
  animationDuration?: number;
}

const toggleRight = keyframes({
  '0%': {
    transform: `translate(1px, 0)`
  },
  '100%': {
    transform: isMobile ? `translate(50px, 0)` : `translate(29px, 0)`
  }
});

const toggleLeft = keyframes({
  '0%': {
    transform: isMobile ? `translate(50px, 0)` : `translate(29px, 0)`
  },
  '100%': {
    transform: `translate(1px, 0)`
  }
});

const backgroundAnimation = keyframes({
  '0%': {
    background: `${theme.color.underlay['01']}`
  },
  '20%': {
    background: `linear-gradient(280deg, ${theme.color.underlay['01']} 10%, ${theme.color.overlay.primary} 100%)`,
    backgroundClip: 'padding-box'
  },
  '60%': {
    background: `linear-gradient(280deg, ${theme.color.underlay['01']} 20%, ${theme.color.overlay.primary} 100%)`,
    backgroundClip: 'padding-box'
  },
  '100%': {
    background: `${theme.color.overlay.primary}`
  }
});

const reverseBackgroundAnimation = keyframes({
  '0%': {
    background: `${theme.color.overlay.primary}`
  },
  '20%': {
    background: `linear-gradient(280deg, ${theme.color.underlay['01']} 10%, ${theme.color.overlay.primary} 100%)`,
    backgroundClip: 'padding-box'
  },
  '60%': {
    background: `linear-gradient(280deg, ${theme.color.underlay['01']} 50%, ${theme.color.overlay.primary} 100%)`,
    backgroundClip: 'padding-box'
  },
  '100%': {
    background: `${theme.color.underlay['01']}`
  }
});

const svgRight = keyframes({
  '0%': {
    maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 50%, rgba(0,0,0,1) 70%)'
  },
  '100%': {
    maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 70%)'
  }
});
const svgLeft = keyframes({
  '0%': {
    maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 70%)'
  },
  '100%': {
    maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 50%, rgba(0,0,0,1) 70%)'
  }
});

const useCSS = makeCSS(
  ({ theme, animationDuration }: ToggleProps & { theme: Theme }) => {
    return {
      container: {
        width: 'fit-content',
        display: 'flex',
        justifyContent: 'center'
      },
      root: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        width: isMobile ? theme.spacing[20] : theme.spacing[12],
        height: isMobile ? theme.spacing[10] : theme.spacing[6],
        borderRadius: theme.borderRadius.lg,
        paddingLeft: isMobile ? theme.spacing[1] : theme.spacing['0,5'],
        paddingRight: isMobile ? theme.spacing[1] : theme.spacing['0,5'],
        backgroundClip: 'padding-box',
        border: `1px solid ${theme.color.line['02']}`,
        cursor: 'pointer',
        '&.disabled': {
          cursor: 'default',
          opacity: '0.4',
          '&:focus': {
            border: `1px solid ${theme.color.line['02']}`,
            boxShadow: 'none'
          }
        },
        '&:focus': {
          border: `1px solid ${theme.color.foreground.primary}`,
          boxShadow: '0px 0px 0px 4px rgba(255, 212, 35, 0.12)',
          outline: 'none'
        },
        '&:active': {
          background: theme.color.overlay.pressed
        }
      },

      defaultContainer: {
        background: `${theme.color.underlay['01']}`,
        '&.disabled': {
          '&:hover': {
            background: theme.color.underlay['01']
          }
        },
        '&:hover': {
          background: `linear-gradient(0deg, ${theme.color.overlay.hovered} 0%,  ${theme.color.overlay.hovered}  100%), ${theme.color.underlay['01']}`
        }
      },
      switchedContainer: {
        background: `${theme.color.overlay.primary}`,
        '&.disabled': {
          background: theme.color.background.secondary,
          '&:hover': {
            background: theme.color.background.secondary
          }
        },
        '&:hover': {
          background: `linear-gradient(0deg, ${theme.color.overlay.hovered} 0%,  ${theme.color.overlay.hovered}  100%), ${theme.color.overlay.primary}`
        }
      },
      toggle: {
        position: 'absolute',
        flexShrink: 0,
        width: isMobile ? theme.spacing[7] : theme.spacing[4],
        height: isMobile ? theme.spacing[7] : theme.spacing[4],
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.color.line['02']}`
      },
      defaultToggle: {
        background: theme.color.overlay['03'],
        border: `1px solid ${theme.color.line['02']}`
      },
      switchedToggle: {
        background: theme.color.foreground.primary,
        border: `1px solid ${theme.color.foreground.primary}`,
        '&.disabled': {
          border: `1px solid ${theme.color.line['02']}`,
          background: theme.color.white[100]
        }
      },
      svg: {
        position: 'absolute',
        // zIndex: -1,
        maskImage:
          'linear-gradient(to right, rgba(0,0,0,0) 50%, rgba(0,0,0,1) 70%)'
      },
      //ANIMATIONS
      toggleAnimation: {
        animation: `${toggleRight} ${animationDuration}ms ease-out forwards`
      },
      reverseToggle: {
        animation: `${toggleLeft} ${animationDuration}ms ease-out forwards`
      },

      // backgroundAnimation: {
      //   animation: `${backgroundAnimation} ${animationDuration}ms ease-out backwards`
      // },
      // reverseBackground: {
      //   animation: `${reverseBackgroundAnimation} ${animationDuration}ms ease-out backwards`
      // },

      svgAnimation: {
        animation: `${svgRight} ${animationDuration}ms ease-out forwards`
      },
      reverseSvg: {
        animation: `${svgLeft} ${animationDuration - animationDuration / 2
          }ms ease-out forwards`
      }
    };
  }
);

export const Toggle = (props: ToggleProps) => {
  const { disabled = false, onChange, value = false, animationDuration: requestedDuration = 175 } = props;

  const intialRender = React.useRef(true);

  const animationDuration = intialRender.current ? 0 : requestedDuration

  const classes = useCSS({ ...props, animationDuration });

  const [switched = value, setSwitch] = useState(value || false);

  //to be able to change the toggle for the state prop
  useEffect(() => {
    setSwitch(value || false);
  }, [value]);

  //when key is pushed
  const handleKeyDown = (event) => {
    //when it's disabled it shouldn't be able to have click events
    if (disabled) {
      console.log('checkBox disabled');
    } else {
      //when ENTER and SPACE are pushed
      //keycode for enter is 13
      //keycode for space is 32
      if (event.keyCode === 13 || event.keyCode === 32) {
        intialRender.current = false

        event.preventDefault();
        setSwitch(!switched);
        if (onChange) {
          onChange(!switched);
        }
      }
    }
  };

  const handleSwitch = () => {
    //when it's disabled it shouldn't be able to have click events
    if (disabled) {
      console.log('Toggle disabled');
    } else {
      intialRender.current = false

      setSwitch(!switched);
      if (onChange) {
        onChange(!switched);
      }
    }
  };

  return (
    <div className={classes.container}>

      {/* 0 / 1  numbers displayed in the background of the toggle */}
      <svg
        className={cx(
          classes.svg,
          switched ? classes.svgAnimation : classes.reverseSvg
        )}
        width={isMobile ? "80" : "48"}
        height={isMobile ? "38" : "24"}
        viewBox="0 0 48 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >

        <circle cx="34" cy="13" r="4" stroke="white"
          strokeOpacity="0.12"
          fill="black"
          fillOpacity="0.12"
        />

        <rect
          x="13"
          y="9"
          width="2"
          height="8"
          fill="white"
          fillOpacity="0.12"
        />
      </svg>

      <div
        onClick={handleSwitch}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className={cx(
          classes.root,
          {
            disabled: disabled
          },
          switched ? classes.switchedContainer : classes.defaultContainer,
          // switched ? classes.backgroundAnimation : classes.reverseBackground,
          classes[animationDuration]
        )}
      >
        <div
          className={cx(
            classes.toggle,
            { disabled: disabled },
            switched ? classes.switchedToggle : classes.defaultToggle,
            switched ? classes.toggleAnimation : classes.reverseToggle
          )}
        ></div>
      </div>
    </div>
  );
};
